import { ErrorBoundary, Layout } from '@/components';
import HomePage from '@/pages';
import ILKPage from '@/pages/collateral/core/ilk';
import VaultsPage from '@/pages/collateral/core/ilk/cdps';
import VaultPage from '@/pages/collateral/core/ilk/cdps/urn';
import UrnActivityPage from '@/pages/collateral/core/ilk/cdps/urn/activity';
import ParametersPage from '@/pages/collateral/core/ilk/parameters';
import * as Sentry from '@sentry/react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import ASCPage from './pages/asc';
import StarASCPage from './pages/asc/star';
import CdpsAtRisk from './pages/atRisk';
import AggregateBackstopPage from './pages/backstop';
import BlockedPage from './pages/blocked';
import AggregateBufferPage from './pages/buffer';
import CollateralPage from './pages/collateral';
import CorePage from './pages/collateral/core';
import CdpsPage from './pages/collateral/core/cdps';
import ILKActivityPage from './pages/collateral/core/ilk/activity';
import ILKStabilityFeePage from './pages/collateral/core/ilk/stabilityFee';
import GrovePage from './pages/collateral/grove';
import GroveIlkActivityPage from './pages/collateral/grove/activity';
import GroveIlkPage from './pages/collateral/grove/ilk';
import LegacyRWAPage from './pages/collateral/legacyRwa';
import LegacyRWAILKPage from './pages/collateral/legacyRwa/ilk';
import LegacyRWAActivityPage from './pages/collateral/legacyRwa/ilk/activity';
import RWAPage from './pages/collateral/rwa';
import RWAILKPage from './pages/collateral/rwa/ilk';
import RWAActivityPage from './pages/collateral/rwa/ilk/activity';
import PortfolioPage from './pages/collateral/rwa/ilk/portfolio';
import SealILKPage from './pages/collateral/sealEngine';
import SealIlkActivityPage from './pages/collateral/sealEngine/activity';
import SealVaultsPage from './pages/collateral/sealEngine/cdps';
import SealVaultPage from './pages/collateral/sealEngine/cdps/urn';
import SealUrnActivityPage from './pages/collateral/sealEngine/cdps/urn/activity';
import SealParametersPage from './pages/collateral/sealEngine/parameters';
import SealActivityPage from './pages/collateral/sealEngine/sealActivity';
import SealStabilityFeePage from './pages/collateral/sealEngine/stabilityFee';
import SparkPage from './pages/collateral/spark';
import SparkIlkActivityPage from './pages/collateral/spark/activity';
import SparkIlkPage from './pages/collateral/spark/ilk';
import MorphoPage from './pages/collateral/spark/morpho/protocol';
import SparkLendPage from './pages/collateral/spark/spark-dai/protocol';
import StablecoinsPage from './pages/collateral/stablecoins';
import StablecoinsILKPage from './pages/collateral/stablecoins/ilk';
import StablecoinsActivityPage from './pages/collateral/stablecoins/ilk/activity';
import ExpensesPage from './pages/expenses';
import ExpensesEntityPage from './pages/expenses/entity';
import EarningsPage from './pages/financials';
import GlossaryPage from './pages/glossary';
import LiquidationsPage from './pages/liquidations';
import IlkLiquidationPage from './pages/liquidations/ilk';
import LiquidationPage from './pages/liquidations/ilk/liquidation';
import DAIMigrationsPage from './pages/migrations/dai';
import DAIMigrationsEventsPage from './pages/migrations/dai/activity';
import MKRMigrationsPage from './pages/migrations/mkr';
import MKRMigrationsEventsPage from './pages/migrations/mkr/activity';
import MigrationsMKRSKYPage from './pages/migrations/mkrsky';
import MKRSKYEventsPage from './pages/migrations/mkrsky/activity';
import MultichainPage from './pages/multichain';
import ArbitrumPage from './pages/multichain/arbitrum';
import ArbitrumBridgePage from './pages/multichain/arbitrum/bridge';
import ArbitrumBridgeActivityPage from './pages/multichain/arbitrum/bridge/activity';
import ArbitrumPSM3Page from './pages/multichain/arbitrum/psm3';
import ArbitrumPSM3ActivityPage from './pages/multichain/arbitrum/psm3/activity';
import ArbitrumPSM3DepositorsPage from './pages/multichain/arbitrum/psm3/depositors';
import ArbitrumPSM3DepositorEventsPage from './pages/multichain/arbitrum/psm3/depositors/walletAddress';
import ArbitrumPSM3SwapActivityPage from './pages/multichain/arbitrum/psm3/swap-activity';
import BasePage from './pages/multichain/base';
import BaseBridgePage from './pages/multichain/base/bridge';
import BridgeActivityPage from './pages/multichain/base/bridge/activity';
import PSM3Page from './pages/multichain/base/psm3';
import PSM3ActivityPage from './pages/multichain/base/psm3/activity';
import PSM3DepositorsPage from './pages/multichain/base/psm3/depositors';
import PSM3DepositorEventsPage from './pages/multichain/base/psm3/depositors/walletAddress';
import PSM3SwapActivityPage from './pages/multichain/base/psm3/swap-activity';
import OptimismPage from './pages/multichain/optimism';
import SolanaPage from './pages/multichain/solana';
import UnichainPage from './pages/multichain/unichain';
import ParametersSummary from './pages/parameters';
import FarmsPage from './pages/rewards';
import FarmPage from './pages/rewards/farm';
import FarmActivityPage from './pages/rewards/farm/activity';
import FarmDespositorsPage from './pages/rewards/farm/depositors';
import FarmDespositorPage from './pages/rewards/farm/depositors/walletAddress';
import RequiredRiskCapitalPage from './pages/rrc';
import StarRequiredRiskCapitalPage from './pages/rrc/star';
import SavingsPage from './pages/savings';
import DSRPage from './pages/savings/dai-savings-rate';
import DSRActivityPage from './pages/savings/dai-savings-rate/activity';
import DSRDepositorsPage from './pages/savings/dai-savings-rate/suppliers';
import SavingsDSRDespositorPage from './pages/savings/dai-savings-rate/suppliers/walletAddress';
import SSRPage from './pages/savings/sky-savings-rate';
import SSRActivityPage from './pages/savings/sky-savings-rate/activity';
import SSRDepositorsPage from './pages/savings/sky-savings-rate/suppliers';
import SavingsSSRDespositorPage from './pages/savings/sky-savings-rate/suppliers/walletAddress';
import SBEPage from './pages/smart-burn-engine';
import SBEActivityPage from './pages/smart-burn-engine/activity';
import StakingPage from './pages/staking';
import StakePage from './pages/staking/stake';
import StakeActivityPage from './pages/staking/stake/activity';
import StakeDespositorsPage from './pages/staking/stake/depositors';
import StakeDespositorPage from './pages/staking/stake/depositors/walletAddress';
import STUSDSPage from './pages/stusds';
import STUSDSSuppliersActivityPage from './pages/stusds/activity';
import STUSDSSuppliersPage from './pages/stusds/suppliers';
import STUSDSSupplierEventsPage from './pages/stusds/suppliers/walletAddress';
import SupplyPage from './pages/supply';
import SurplusActivityPage from './pages/surplus/activity';
import TokensPage from './pages/tokens';
import TokenPage from './pages/tokens/token';
import TokenActivityPage from './pages/tokens/token/activity';
import TokenHoldersPage from './pages/tokens/token/holders';
import TokensHolderPage from './pages/tokens/token/holders/walletAddress';
import ArbitrumTokenPage from './pages/tokens/tokenArbitrum';
import TokensArbitrumPage from './pages/tokens/tokenArbitrum/tokens';
import TokensActivityArbitrumPage from './pages/tokens/tokenArbitrum/tokens/activity';
import TokenHoldersArbitrumPage from './pages/tokens/tokenArbitrum/tokens/holders';
import TokenHolderEventsArbitrumPage from './pages/tokens/tokenArbitrum/tokens/holders/walletAddress';
import BaseTokenPage from './pages/tokens/tokenBase';
import TokensBasePage from './pages/tokens/tokenBase/tokens';
import TokensActivityBasePage from './pages/tokens/tokenBase/tokens/activity';
import TokenHoldersBasePage from './pages/tokens/tokenBase/tokens/holders';
import TokenHolderEventsBasePage from './pages/tokens/tokenBase/tokens/holders/walletAddress';
import TokensOptimismPage from './pages/tokens/tokenOptimism';
import TokenOptimismPage from './pages/tokens/tokenOptimism/tokens';
import TokensUnichainPage from './pages/tokens/tokenUnichain';
import TokenUnichainPage from './pages/tokens/tokenUnichain/tokens';
import UpgradePage from './pages/upgrade';
import UpgradeUSDSPage from './pages/upgrade/usds';
import UpgradeUSDSActivityPage from './pages/upgrade/usds/activity';
import UpgradeCallersPage from './pages/upgrade/usds/callers';
import UpgradeCallerPage from './pages/upgrade/usds/callers/walletAddress';
import UpgradeWalletsPage from './pages/upgrade/usds/wallets';
import UpgradeWalletPage from './pages/upgrade/usds/wallets/walletAddress';

import BuybackPage from './pages/buyback';
import BuybackActivityPage from './pages/buyback/activity';

import StakingEngineILKPage from './pages/collateral/stakingEngine';
import StakingEngineActivityPage from './pages/collateral/stakingEngine/activity';
import StakingEngineVaultsPage from './pages/collateral/stakingEngine/cdps';
import StakingEngineVaultPage from './pages/collateral/stakingEngine/cdps/urn';
import StakingEngineUrnActivityPage from './pages/collateral/stakingEngine/cdps/urn/activity';
import StakingEngineParametersPage from './pages/collateral/stakingEngine/parameters';
import StakingEngineStabilityFeePage from './pages/collateral/stakingEngine/stabilityFee';
import StakingEngineStakingActivityPage from './pages/collateral/stakingEngine/stakingActivity';

import KeelPage from './pages/collateral/keel';
import KeelIlkActivityPage from './pages/collateral/keel/activity';
import KeelIlkPage from './pages/collateral/keel/ilk';
import ObexPage from './pages/collateral/obex';
import ObexIlkActivityPage from './pages/collateral/obex/activity';
import ObexIlkPage from './pages/collateral/obex/ilk';
import OseroPage from './pages/collateral/osero';
import OseroIlkActivityPage from './pages/collateral/osero/activity';
import OseroIlkPage from './pages/collateral/osero/ilk';

let routes = [
  {
    path: '/',
    element: <HomePage />
  },
  {
    path: '/base/*',
    element: <Navigate to={`/multichain${window.location.pathname}`} replace />
  },
  {
    path: '/cdps-at-risk',
    element: <CdpsAtRisk />
  },
  {
    path: '/collateral',
    element: <CollateralPage />
  },
  {
    path: '/collateral/core',
    element: <CorePage />
  },
  {
    path: '/collateral/spark',
    element: <SparkPage />
  },
  {
    path: '/collateral/keel',
    element: <KeelPage />
  },
  {
    path: '/collateral/keel/:ilk',
    element: <KeelIlkPage />
  },
  {
    path: '/collateral/keel/:ilk/activity',
    element: <KeelIlkActivityPage />
  },
  {
    path: '/collateral/obex',
    element: <ObexPage />
  },
  {
    path: '/collateral/obex/:ilk',
    element: <ObexIlkPage />
  },
  {
    path: '/collateral/obex/:ilk/activity',
    element: <ObexIlkActivityPage />
  },
  {
    path: '/collateral/osero',
    element: <OseroPage />
  },
  {
    path: '/collateral/osero/:ilk',
    element: <OseroIlkPage />
  },
  {
    path: '/collateral/osero/:ilk/activity',
    element: <OseroIlkActivityPage />
  },
  {
    path: '/collateral/grove',
    element: <GrovePage />
  },
  {
    path: '/collateral/grove/:ilk',
    element: <GroveIlkPage />
  },
  {
    path: '/collateral/grove/:ilk/activity',
    element: <GroveIlkActivityPage />
  },
  {
    path: '/collateral/core/cdps',
    element: <CdpsPage />
  },
  {
    path: '/liquidations',
    element: <LiquidationsPage />
  },
  {
    path: '/liquidations/:ilk',
    element: <IlkLiquidationPage />
  },
  {
    path: '/liquidations/:ilk/:auction_id',
    element: <LiquidationPage />
  },
  {
    path: '/collateral/core/:ilk',
    element: <ILKPage />
  },
  {
    path: '/collateral/core/:ilk/parameters/stability-fee',
    element: <ILKStabilityFeePage />
  },
  {
    path: '/collateral/core/:ilk/activity',
    element: <ILKActivityPage />
  },
  {
    path: '/collateral/:group/:ilk/parameters',
    element: <ParametersPage />
  },
  {
    path: '/collateral/core/:ilk/cdps',
    element: <VaultsPage />
  },
  {
    path: '/collateral/core/:ilk/cdps/:urn',
    element: <VaultPage />
  },
  {
    path: '/collateral/core/:ilk/cdps/:urn/activity',
    element: <UrnActivityPage />
  },
  {
    path: '/collateral/legacy-rwa',
    element: <LegacyRWAPage />
  },
  {
    path: '/collateral/legacy-rwa/:ilk',
    element: <LegacyRWAILKPage />
  },
  {
    path: '/collateral/stablecoins',
    element: <StablecoinsPage />
  },
  {
    path: '/collateral/stablecoins/:ilk',
    element: <StablecoinsILKPage />
  },
  {
    path: '/collateral/rwa',
    element: <RWAPage />
  },
  {
    path: '/collateral/rwa/:ilk',
    element: <RWAILKPage />
  },
  {
    path: '/collateral/rwa/:ilk/portfolio',
    element: <PortfolioPage />
  },
  {
    path: '/collateral/spark/DIRECT-SPARK-MORPHO-DAI/morpho',
    element: <MorphoPage />
  },
  {
    path: '/collateral/spark/:ilk',
    element: <SparkIlkPage />
  },
  {
    path: '/collateral/spark/DIRECT-SPARK-DAI/spark',
    element: <SparkLendPage />
  },
  {
    path: '/collateral/seal-engine/',
    element: <SealILKPage />
  },
  {
    path: '/collateral/seal-engine/activity',
    element: <SealIlkActivityPage />
  },
  {
    path: '/collateral/seal-engine/seal-activity',
    element: <SealActivityPage />
  },
  {
    path: '/collateral/seal-engine/cdps',
    element: <SealVaultsPage />
  },
  {
    path: '/collateral/seal-engine/cdps/:urn',
    element: <SealVaultPage />
  },
  {
    path: '/collateral/seal-engine/cdps/:urn/activity',
    element: <SealUrnActivityPage />
  },
  {
    path: '/collateral/seal-engine/parameters',
    element: <SealParametersPage />
  },
  {
    path: '/collateral/seal-engine/parameters/stability-fee',
    element: <SealStabilityFeePage />
  },
  {
    path: '/upgrade-dai-to-usds',
    element: <DAIMigrationsPage />
  },
  {
    path: '/upgrade-dai-to-usds/activity',
    element: <DAIMigrationsEventsPage />
  },
  {
    path: '/upgrade-mkr-to-sky',
    element: <MKRMigrationsPage />
  },
  {
    path: '/upgrade-mkr-to-sky/activity',
    element: <MKRMigrationsEventsPage />
  },
  {
    path: '/supply',
    element: <SupplyPage />
  },
  {
    path: '/tokens',
    element: <TokensPage />
  },
  {
    path: '/tokens/:address',
    element: <TokenPage />
  },
  {
    path: '/tokens/:address/holders',
    element: <TokenHoldersPage />
  },
  {
    path: '/tokens/:address/holders/:walletAddress',
    element: <TokensHolderPage />
  },
  {
    path: '/tokens/:address/activity',
    element: <TokenActivityPage />
  },
  {
    path: '/tokens/base/:address',
    element: <TokensBasePage />
  },
  {
    path: '/tokens/base',
    element: <BaseTokenPage />
  },
  {
    path: '/tokens/base/:address/holders',
    element: <TokenHoldersBasePage />
  },
  {
    path: '/tokens/base/:address/holders/:walletAddress',
    element: <TokenHolderEventsBasePage />
  },
  {
    path: '/tokens/base/:address/activity',
    element: <TokensActivityBasePage />
  },
  {
    path: '/tokens/arbitrum/:address',
    element: <TokensArbitrumPage />
  },
  {
    path: '/tokens/arbitrum',
    element: <ArbitrumTokenPage />
  },
  {
    path: '/tokens/arbitrum/:address/holders',
    element: <TokenHoldersArbitrumPage />
  },
  {
    path: '/tokens/arbitrum/:address/holders/:walletAddress',
    element: <TokenHolderEventsArbitrumPage />
  },
  {
    path: '/tokens/arbitrum/:address/activity',
    element: <TokensActivityArbitrumPage />
  },
  {
    path: '/capital-buffer',
    element: <AggregateBufferPage />
  },
  {
    path: '/capital-backstop',
    element: <AggregateBackstopPage />
  },
  {
    path: '/surplus',
    element: <Navigate to={`/capital-backstop`} replace />
  },
  {
    path: '/surplus/activity',
    element: <SurplusActivityPage />
  },
  {
    path: '/smart-burn-engine',
    element: <SBEPage />
  },
  {
    path: '/smart-burn-engine/activity',
    element: <SBEActivityPage />
  },
  {
    path: '/buyback',
    element: <BuybackPage />
  },
  {
    path: '/buyback/activity',
    element: <BuybackActivityPage />
  },
  {
    path: '/parameters',
    element: <ParametersSummary />
  },
  {
    path: '/collateral/legacy-rwa/:ilk/activity',
    element: <LegacyRWAActivityPage />
  },
  {
    path: '/collateral/rwa/:ilk/activity',
    element: <RWAActivityPage />
  },
  {
    path: '/collateral/stablecoins/:ilk/activity',
    element: <StablecoinsActivityPage />
  },
  {
    path: '/collateral/spark/:ilk/activity',
    element: <SparkIlkActivityPage />
  },
  {
    path: '/financials',
    element: <EarningsPage />
  },
  {
    path: '/expenses',
    element: <ExpensesPage />
  },
  {
    path: '/glossary',
    element: <GlossaryPage />
  },
  {
    path: '/expenses/:address',
    element: <ExpensesEntityPage />
  },
  {
    path: '/upgrade',
    element: <UpgradePage />
  },
  {
    path: '/upgrade/usds',
    element: <UpgradeUSDSPage />
  },
  {
    path: '/upgrade/usds/wallets',
    element: <UpgradeWalletsPage />
  },
  {
    path: '/upgrade/usds/wallets/:walletAddress',
    element: <UpgradeWalletPage />
  },
  {
    path: '/upgrade/usds/callers',
    element: <UpgradeCallersPage />
  },
  {
    path: '/upgrade/usds/callers/:walletAddress',
    element: <UpgradeCallerPage />
  },
  {
    path: '/upgrade/usds/activity',
    element: <UpgradeUSDSActivityPage />
  },
  {
    path: '/upgrade/sky',
    element: <MKRMigrationsPage />
  },
  {
    path: '/upgrade/sky/activity',
    element: <MKRMigrationsEventsPage />
  },
  {
    path: '/upgrade/sky/upgrade-v1',
    element: <MKRMigrationsPage />
  },
  {
    path: '/upgrade/sky/upgrade-v1/activity',
    element: <MKRMigrationsEventsPage />
  },
  {
    path: '/upgrade/sky/upgrade-v2',
    element: <MigrationsMKRSKYPage />
  },
  {
    path: '/upgrade/sky/upgrade-v2/activity',
    element: <MKRSKYEventsPage />
  },
  {
    path: '/multichain',
    element: <MultichainPage />
  },
  {
    path: '/multichain/solana',
    element: <SolanaPage />
  },
  {
    path: '/multichain/base',
    element: <BasePage />
  },
  {
    path: '/multichain/base/skylink',
    element: <BaseBridgePage />
  },
  {
    path: '/multichain/base/skylink/activity',
    element: <BridgeActivityPage />
  },
  {
    path: '/multichain/base/psm3',
    element: <PSM3Page />
  },
  {
    path: '/multichain/base/psm3/activity',
    element: <PSM3ActivityPage />
  },
  {
    path: '/multichain/base/psm3/swap-activity',
    element: <PSM3SwapActivityPage />
  },
  {
    path: '/multichain/base/psm3/depositors',
    element: <PSM3DepositorsPage />
  },
  {
    path: '/multichain/base/psm3/depositors/:walletAddress',
    element: <PSM3DepositorEventsPage />
  },
  {
    path: '/multichain/arbitrum',
    element: <ArbitrumPage />
  },
  {
    path: '/multichain/optimism',
    element: <OptimismPage />
  },
  {
    path: '/multichain/unichain',
    element: <UnichainPage />
  },
  {
    path: '/multichain/arbitrum/skylink',
    element: <ArbitrumBridgePage />
  },
  {
    path: '/multichain/arbitrum/skylink/activity',
    element: <ArbitrumBridgeActivityPage />
  },
  {
    path: '/multichain/arbitrum/psm3',
    element: <ArbitrumPSM3Page />
  },
  {
    path: '/multichain/arbitrum/psm3/activity',
    element: <ArbitrumPSM3ActivityPage />
  },
  {
    path: '/multichain/arbitrum/psm3/swap-activity',
    element: <ArbitrumPSM3SwapActivityPage />
  },
  {
    path: '/multichain/arbitrum/psm3/depositors',
    element: <ArbitrumPSM3DepositorsPage />
  },
  {
    path: '/multichain/arbitrum/psm3/depositors/:walletAddress',
    element: <ArbitrumPSM3DepositorEventsPage />
  },
  {
    path: '/collateral/staking-engine/',
    element: <StakingEngineILKPage />
  },
  {
    path: '/collateral/staking-engine/activity',
    element: <StakingEngineActivityPage />
  },
  {
    path: '/collateral/staking-engine/staking-activity',
    element: <StakingEngineStakingActivityPage />
  },
  {
    path: '/collateral/staking-engine/cdps',
    element: <StakingEngineVaultsPage />
  },
  {
    path: '/collateral/staking-engine/cdps/:urn',
    element: <StakingEngineVaultPage />
  },
  {
    path: '/collateral/staking-engine/cdps/:urn/activity',
    element: <StakingEngineUrnActivityPage />
  },
  {
    path: '/collateral/staking-engine/parameters',
    element: <StakingEngineParametersPage />
  },
  {
    path: '/collateral/staking-engine/parameters/stability-fee',
    element: <StakingEngineStabilityFeePage />
  },
  {
    path: '/tokens/optimism/:address',
    element: <TokenOptimismPage />
  },
  {
    path: '/tokens/optimism',
    element: <TokensOptimismPage />
  },
  {
    path: '/tokens/unichain/:address',
    element: <TokenUnichainPage />
  },
  {
    path: '/tokens/unichain',
    element: <TokensUnichainPage />
  },
  {
    path: '/actively-stabilizing-collateral',
    element: <ASCPage />
  },
  {
    path: '/actively-stabilizing-collateral/:star',
    element: <StarASCPage />
  },
  {
    path: '/required-risk-capital',
    element: <RequiredRiskCapitalPage />
  },
  {
    path: '/required-risk-capital/:star',
    element: <StarRequiredRiskCapitalPage />
  },
  {
    path: '/star-monitoring',
    element: <Navigate to={`/required-risk-capital`} replace />
  },
  {
    path: '/revenue',
    element: <Navigate to={`/financials`} replace />
  },
  {
    path: '/accounting/*',
    element: (
      <Navigate
        to={`/expenses${window.location.pathname.replace('/accounting', '')}`}
        replace
      />
    )
  },
  {
    path: '/stusds/stakers/*',
    element: (
      <Navigate
        to={`/stusds/suppliers${window.location.pathname.replace('/stusds/stakers', '')}`}
        replace
      />
    )
  }
];

// @ts-ignore
if (import.meta.env.VITE_PUBLIC_RESTRICTED_BUILD !== 'true') {
  const restrictedRoutes = [
    {
      path: '/rewards',
      element: <FarmsPage />
    },
    {
      path: '/staking',
      element: <StakingPage />
    },
    {
      path: '/rewards/:address',
      element: <FarmPage />
    },
    {
      path: '/staking/:address',
      element: <StakePage />
    },
    {
      path: '/rewards/:address/activity',
      element: <FarmActivityPage />
    },
    {
      path: '/staking/:address/activity',
      element: <StakeActivityPage />
    },
    {
      path: '/rewards/:address/suppliers',
      element: <FarmDespositorsPage />
    },
    {
      path: '/staking/:address/stakers',
      element: <StakeDespositorsPage />
    },
    {
      path: '/rewards/:address/suppliers/:walletAddress',
      element: <FarmDespositorPage />
    },
    {
      path: '/staking/:address/stakers/:walletAddress',
      element: <StakeDespositorPage />
    },
    {
      path: '/savings',
      element: <SavingsPage />
    },
    {
      path: '/savings/dai-savings-rate',
      element: <DSRPage />
    },
    {
      path: '/savings/dai-savings-rate/suppliers',
      element: <DSRDepositorsPage />
    },
    {
      path: '/savings/dai-savings-rate/activity',
      element: <DSRActivityPage />
    },
    {
      path: '/savings/dai-savings-rate/suppliers/:walletAddress',
      element: <SavingsDSRDespositorPage />
    },
    {
      path: '/savings/sky-savings-rate',
      element: <SSRPage />
    },
    {
      path: '/savings/sky-savings-rate/suppliers',
      element: <SSRDepositorsPage />
    },
    {
      path: '/savings/sky-savings-rate/activity',
      element: <SSRActivityPage />
    },
    {
      path: '/savings/sky-savings-rate/suppliers/:walletAddress',
      element: <SavingsSSRDespositorPage />
    },
    {
      path: '/stusds',
      element: <STUSDSPage />
    },
    {
      path: '/stusds/activity',
      element: <STUSDSSuppliersActivityPage />
    },
    {
      path: '/stusds/suppliers',
      element: <STUSDSSuppliersPage />
    },
    {
      path: '/stusds/suppliers/:walletAddress',
      element: <STUSDSSupplierEventsPage />
    },
    {
      path: '/stusds/suppliers/:walletAddress/activity',
      element: <STUSDSSupplierEventsPage />
    },
    {
      path: '/stusds/suppliers/:walletAddress/historic',
      element: <STUSDSSupplierEventsPage />
    }
  ];

  routes = routes.concat(restrictedRoutes);
}

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouter(createBrowserRouter);

const router = sentryCreateBrowserRouter([
  {
    element: <BlockedPage />,
    path: '/blocked'
  },
  {
    element: <Layout />,
    errorElement: (
      <Layout>
        <ErrorBoundary />
      </Layout>
    ),
    children: routes
  }
]);

export default router;
